/**
 * @file src/pages/Maintenance.tsx
 * @fileoverview Defines the Maintenance page component.
 * This page is displayed when the application is in maintenance mode.
 * It informs users about the temporary unavailability and shows version information.
 */
import React, { useState, useEffect } from 'react';
import { useI18n } from '../i18n';
import { useVersionInfo } from '../hooks/useVersionInfo';
import { getAppEnvLabel } from '../utils/appEnv';
import { motion } from 'framer-motion';
import BootstrapRecovery from '../components/BootstrapRecovery';
import MaintenanceHockeyField from '../components/MaintenanceHockeyField';
import usePageView from '../hooks/usePageView';
import { getBackendVersionApi, getBackendVersionValue } from '../lib/versionApi';

/**
 * Maintenance is een pagina die wordt weergegeven wanneer de applicatie in onderhoudsmodus is.
 * Het informeert gebruikers over de tijdelijke onbeschikbaarheid en toont versie-informatie.
 */
const Maintenance: React.FC = () => {
  const { t } = useI18n();
  const [aiVersion, setAiVersion] = useState<string>('');
  const versionInfo = useVersionInfo();
  const mode = getAppEnvLabel();
  usePageView('maintenance', { page: 'maintenance' });

  useEffect(() => {
    const fetchAiVersion = async () => {
      try {
        const data = await getBackendVersionApi();
        setAiVersion(getBackendVersionValue(data));
      } catch (error) {
        console.error('Failed to fetch AI version:', error);
      }
    };

    void fetchAiVersion();
  }, []);

  const getBuildDisplay = () => {
    return versionInfo ? `#${versionInfo.buildNumber}` : '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col items-center justify-center text-center p-6 overflow-hidden">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-12"
      >
        <img 
          src="/we-dohockey-orange-black-trans.png" 
          alt="doHockey" 
          className="h-24 mx-auto mb-8 transition-transform duration-300 hover:scale-105"
        />
        <h1 className="text-4xl font-bold text-gray-800">
          {t('maintenance_title')}
        </h1>
      </motion.div>

      <div className="mb-6 w-full max-w-2xl text-left"><BootstrapRecovery onlyWhenRecoveryNeeded /></div>
      <MaintenanceHockeyField />
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="max-w-2xl w-full"
      >
        
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <p className="text-lg text-gray-700 mb-4">
            {t('maintenance_working')}
          </p>
          <p className="text-gray-600">
            {t('maintenance_apology')}
          </p>
          <a
            href="mailto:hello@dohockey.app"
            className="mt-3 inline-flex min-h-11 items-center text-orange-600 underline underline-offset-4 hover:text-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange-600"
          >
            hello@dohockey.app
          </a>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-gray-500">
          <span>Platform v{versionInfo?.version}{getBuildDisplay()}</span>
          <span className="hidden sm:inline">|</span>
          <span>AI Engines v{aiVersion}</span>
          <span className="hidden sm:inline">|</span>
          <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">{mode}</span>
        </div>
        
        <div className="mt-8">
          <div className="inline-flex items-center text-orange-500 font-medium">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {t('maintenance_updating')}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Maintenance;
